/**
 * WHERE an inserted PDApp icon lands, relative to the element the author
 * selected (#788, Chu's third review).
 *
 * Its own module, and not part of `HtmlEditor`, for the ordinary reason: it is a
 * pure function over a source string and a parsed `loc`, with four branches and
 * a corroboration rule in each — the kind of thing that should be TESTED by
 * calling it, not by grepping the component that happens to use it.
 */
/** ONE definition, from `@papan/types`, because the chat path already uses it to
 *  validate the browser's reply — a second copy here would let the two surfaces
 *  drift about what placements exist. `after` is the default and the historical
 *  behaviour. */
export type { Placement as IconPlacement } from '@papan/types'
import type { Placement as IconPlacement } from '@papan/types'

/**
 * The offset an insert should write at, for one placement.
 *
 * Each branch CORROBORATES the offset against the source before returning it,
 * the way every sibling design write-back does — a loc that has drifted must
 * fall through to the document-level insert rather than splice into the middle
 * of a text node. `null` means "this placement is not trustworthy here".
 *
 * `innerEnd` is present only for an element parse5 saw an explicit end tag for,
 * which is exactly the set that can hold children — so its absence is what makes
 * the two "inside" options unavailable rather than wrong.
 */
export function placementOffset(
  src: string,
  t: number[],
  placement: IconPlacement,
  adjust: (n: number, isEnd?: boolean) => number,
): number | null {
  const at = (n: number, isEnd = false) => {
    const v = adjust(n, isEnd)
    return Number.isFinite(v) && v >= 0 && v <= src.length ? v : null
  }
  if (placement === 'before') {
    const v = at(t[4])
    return v !== null && src[v] === '<' ? v : null
  }
  if (placement === 'inside-start') {
    if (t.length < 8) return null
    const v = at(t[5])
    return v !== null && src.slice(0, v).endsWith('>') ? v : null
  }
  if (placement === 'inside-end') {
    if (t.length < 8) return null
    const v = at(t[7], true)
    return v !== null && src[v] === '<' ? v : null
  }
  if (placement === 'after') {
    const v = at(t[6], true)
    // `>` before it (an explicit end tag or a self-closing one), or `<` at it —
    // which is what an IMPLICITLY closed element gives.
    return v !== null && (src.slice(0, v).endsWith('>') || src[v] === '<') ? v : null
  }
  // Exhaustive. A fifth placement must not fall through to "after" and silently
  // mean something the author did not choose — `never` makes it a compile error.
  const exhaustive: never = placement
  void exhaustive
  return null
}
